import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Col,
  DatePicker,
  InputNumber,
  Modal,
  notification,
  Row,
  Space,
  Switch,
  Typography,
} from 'antd';
import type { RangePickerProps } from 'antd/lib/date-picker';
import { downloadPresetReturns, fetchMissingEodPrices, selectors } from 'state';

type Props = {
  preset_name: string;
  preset_id: number;
  visible: boolean;
  hide: () => void;
};

const SeePresetReturnsModal: React.FC<Props> = ({
  preset_name,
  preset_id,
  visible,
  hide,
}) => {
  const dispatch = useDispatch();
  const waiting = useSelector(selectors.waiting);

  const [sessRange, setSessRange] = useState<{
    moments: any[] | null;
    strings: [string, string] | null;
  }>({
    moments: null,
    strings: null,
  });

  const [frequency, setFrequency] = useState<number | null>(null);
  const [samples, setSamples] = useState<number | null>(null);

  const [sessFetchEodOnly, setSessFetchEodOnly] = useState<boolean>(false);

  const onSessChange: RangePickerProps['onChange'] = (moments, dateStrings) =>
    setSessRange({
      moments: moments as any[] | null,
      strings: (dateStrings as [string, string]) ?? null,
    });

  const onFreqChange = (value: number | undefined) => {
    if (value !== undefined) setFrequency(value);
  };

  const onSampleChange = (value: number | undefined) => {
    if (value !== undefined) setSamples(value);
  };

  const downloadFinalReturnsCsv = () => {
    if (!sessRange.strings) {
      notification.warning({ message: 'Set date range' });
      return;
    }
    if (!frequency) {
      notification.warning({ message: 'Set frequency' });
      return;
    }
    dispatch(
      downloadPresetReturns({
        preset_id,
        start_date: sessRange.strings[0],
        end_date: sessRange.strings[1],
        preset_name,
        get_per_period_returns: false,
        samples: samples,
        frequency: frequency,
      }) as any
    );
  };

  const downloadPerPeriodReturnsCsv = () => {
    if (!sessRange.strings) {
      notification.warning({ message: 'Set date range' });
      return;
    }
    if (!frequency) {
      notification.warning({ message: 'Set frequency' });
      return;
    }
    dispatch(
      downloadPresetReturns({
        preset_id,
        start_date: sessRange.strings[0],
        end_date: sessRange.strings[1],
        preset_name,
        get_per_period_returns: true,
        samples: null,
        frequency: frequency,
      }) as any
    );
  };

  const onCancel = () => hide();

  return (
    <Modal
      title={`Download returns for preset: ${preset_name}`}
      open={visible}
      onCancel={onCancel}
      footer={[]}
      // width={800}
    >
      <Row gutter={30}>
        <Col
          // span={12}
          style={{
            borderLeft: '2px solid #eee',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            paddingRight: 20,
          }}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {/*<Space size="small">*/}
            {/*  <Switch*/}
            {/*    checked={sessFetchEodOnly}*/}
            {/*    onChange={setSessFetchEodOnly}*/}
            {/*    size="small"*/}
            {/*  />*/}
            {/*  <Typography.Text>*/}
            {/*    View <code>fetch_eod = True</code> assets instead*/}
            {/*  </Typography.Text>*/}
            {/*</Space>*/}

            <DatePicker.RangePicker
              format="YYYY-MM-DD"
              style={{ width: '100%', maxWidth: 250 }}
              value={sessRange.moments as any}
              onChange={onSessChange}
            />

            <InputNumber
              style={{ width: 120 }}
              placeholder="Frequency"
              value={frequency ?? undefined}
              min={0}
              onChange={onFreqChange}
            />
            {/*<Space direction={'horizontal'}>*/}
            <Typography.Text type={'secondary'}>
              (4 sample points example: shows returns for periods 13, 26, 39,
              final)
            </Typography.Text>
            <InputNumber
              style={{ width: 120 }}
              placeholder="Samples"
              value={samples ?? undefined}
              min={0}
              onChange={onSampleChange}
            />
            {/*</Space>*/}
            <Space direction={'horizontal'}>
              <Button
                loading={waiting}
                type="primary"
                onClick={downloadPerPeriodReturnsCsv}
                disabled={!sessRange.strings || !frequency}
              >
                Download All Periods Returns
              </Button>
              <Button
                loading={waiting}
                type="primary"
                onClick={downloadFinalReturnsCsv}
                disabled={!sessRange.strings || !frequency || !samples}
              >
                Download Sampled Returns
              </Button>
            </Space>
          </Space>
        </Col>
      </Row>
    </Modal>
  );
};

export default SeePresetReturnsModal;
